import {
  TbArrowDownLeft,
  TbArrowUpRight,
  TbBuildingStore,
  TbCash,
  TbClock,
  TbRepeat,
} from "react-icons/tb";

export const PIX_LIMIT_PERIODS = {
  diurno: {
    id: "diurno",
    label: "Diurno",
    hours: "6h às 20h",
    description: "Limites válidos no período diurno, das 6h às 20h.",
  },
  noturno: {
    id: "noturno",
    label: "Noturno",
    hours: "20h às 6h",
    description: "Limites válidos no período noturno, das 20h às 6h.",
  },
};

/**
 * BaaS `AccountPixLimit.Type` → stable `type_code` (see PixLimitTypes.cs).
 * 1 enviado · 2 recebido · 3 saque_troco · 4 pj · 5 agendado · 6 automatico
 */
export const PIX_LIMIT_TYPE_CODES = {
  1: "enviado",
  2: "recebido",
  3: "saque_troco",
  4: "pj",
  5: "agendado",
  6: "automatico",
};

/** Icons / labels / descriptions keyed by BaaS `type_code`. */
export const PIX_LIMIT_CATEGORY_META = {
  enviado: {
    label: "Pix enviado",
    description: "Transferências Pix para outras contas",
    icon: TbArrowUpRight,
  },
  recebido: {
    label: "Pix recebido",
    description: "Valores recebidos via Pix",
    icon: TbArrowDownLeft,
  },
  saque_troco: {
    label: "Pix Saque e Troco",
    description: "Saques em estabelecimentos credenciados",
    icon: TbCash,
  },
  pj: {
    label: "Pix para pessoa jurídica",
    description: "Pagamentos a empresas e CNPJ",
    icon: TbBuildingStore,
  },
  agendado: {
    label: "Pix agendado",
    description: "Transferências programadas para data futura",
    icon: TbClock,
  },
  automatico: {
    label: "Pix Automático",
    description: "Pagamentos recorrentes autorizados",
    icon: TbRepeat,
  },
};

/** Resolve stable type_code from policy/available item (number, code, or id). */
export const resolvePixLimitTypeCode = (item = {}) => {
  const rawCode = String(
    item.type_code || item.typeCode || item.id || "",
  ).trim();
  if (rawCode && PIX_LIMIT_CATEGORY_META[rawCode]) return rawCode;

  const typeNum = Number(item.type);
  if (Number.isFinite(typeNum) && PIX_LIMIT_TYPE_CODES[typeNum]) {
    return PIX_LIMIT_TYPE_CODES[typeNum];
  }

  // Numeric id mistaken for type (policies often only send `type`).
  if (/^\d+$/.test(rawCode) && PIX_LIMIT_TYPE_CODES[Number(rawCode)]) {
    return PIX_LIMIT_TYPE_CODES[Number(rawCode)];
  }

  return rawCode || null;
};

/**
 * Human-readable limit name. Prefers API `label` when it is not a raw code/number.
 */
export const resolvePixLimitLabel = (item = {}) => {
  const code = resolvePixLimitTypeCode(item);
  const meta = code ? PIX_LIMIT_CATEGORY_META[code] : null;
  const apiLabel = String(item.label || "").trim();

  if (apiLabel) {
    const looksRaw =
      /^\d+$/.test(apiLabel) ||
      /^type_\d+$/i.test(apiLabel) ||
      (code && apiLabel.toLowerCase() === code);
    if (!looksRaw) return apiLabel;
  }

  if (meta?.label) return meta.label;
  if (code) return code;
  if (item.type != null) return `Tipo ${item.type}`;
  return "Limite";
};

export const resolvePixLimitMeta = (item = {}) => {
  const code = resolvePixLimitTypeCode(item);
  const meta = (code && PIX_LIMIT_CATEGORY_META[code]) || {};
  return {
    code,
    label: resolvePixLimitLabel(item),
    description: item.description || meta.description || "",
    icon: meta.icon || PIX_LIMIT_CATEGORY_META.enviado.icon,
    comingSoon: Boolean(item.coming_soon ?? item.comingSoon) || code === "automatico",
  };
};

/**
 * Progress for limit bars/labels.
 * - used === 0 → 0% empty bar
 * - 0 < exact < 1 → bar at least 1%, isLessThanOne (copy: "menos de 1%…", never "<")
 * - otherwise rounded 0–100, capped at 100
 */
export const resolveLimitProgress = (usedCents, totalCents) => {
  const used = Number(usedCents) || 0;
  const total = Number(totalCents) || 0;
  if (total <= 0 || used <= 0) {
    return { exact: 0, barPercent: 0, labelPercent: 0, isLessThanOne: false };
  }

  const exact = Math.min(100, (used / total) * 100);
  if (exact <= 0) {
    return { exact: 0, barPercent: 0, labelPercent: 0, isLessThanOne: false };
  }
  if (exact < 1) {
    return { exact, barPercent: 1, labelPercent: 1, isLessThanOne: true };
  }

  const rounded = Math.min(100, Math.round(exact));
  return { exact, barPercent: rounded, labelPercent: rounded, isLessThanOne: false };
};

/** Rounded percent 0–100 for tone / summary math (sub-1% → 0). */
export const calcLimitPercent = (usedCents, totalCents) => {
  const { exact, isLessThanOne } = resolveLimitProgress(usedCents, totalCents);
  if (isLessThanOne) return 0;
  return Math.min(100, Math.max(0, Math.round(exact)));
};

export const getLimitTone = (percent) => {
  if (percent >= 90) return "alert";
  if (percent >= 75) return "gold";
  return "success";
};

export const resolveLimitAvailableCents = (limit = {}) => {
  const explicit = limit.availableCents ?? limit.available_cents;
  if (explicit != null && Number.isFinite(Number(explicit))) {
    return Math.max(0, Math.round(Number(explicit)));
  }

  const totalCents = Number(limit.totalCents ?? limit.total_cents) || 0;
  const usedCents = Number(limit.usedCents ?? limit.used_cents) || 0;
  return Math.max(0, totalCents - usedCents);
};
